/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
// utils

// material
import { Page, Content, Division } from '@insticator/insticator-ui'
import Header from '../../../../material/structure/header/header'
import GlobalFilter from '../../../../material/globalfilter/globalfilter'
import PageHeader from '../../../../material/structure/pageheader/pageheader'

// styles
import injectSheet from 'react-jss'
import styles from './supplysideplatform_styles'
import RevenueVisualization from '../revenue_beta/revenue_beta'

// static
import {
  NET_REVENUE,
  GROSS_REVENUE,
  AVG_CPM,
  SITE,
  DEVICE,
  GEO,
  PRODUCT_TYPE,
  TOTAL_IMPRESSIONS,
  BAR_CHART,
  LINE_CHART,
  AD_UNIT,
  TOOLTIP_CONTENTS,
  AD_REQUEST,
  BID_RATE,
  WIN_RATE, REVENUE_TYPE
} from '../consts'

/* ========== ~~~~~~~~~~ MONETIZATION - SUPPLY SIDE PLATFORM PAGE ~~~~~~~~~~ ========== */
const SupplySidePlatformPage = injectSheet(styles)((props) => {
  const accountUUID = useSelector((state) => state.session.accountUUID)
  const startDate = useSelector((state) => state.app.filters.dateRange.from)
  const endDate = useSelector((state) => state.app.filters.dateRange.to)
  const compareTo = useSelector((state) => state.app.filters.compareTo)
  const devices = useSelector((state) => state.app.filters.devices)
  const countries = useSelector((state) => state.app.filters.countries)
  const siteUUIDs = useSelector((state) => state.app.filters.sites)
  const lastUpdated = useSelector((state) => state.revenueBeta.lastUpdated)
  const mocked = useSelector((state) => state.session.mocked)
  const { showMessage, message } = useSelector((state) => state.settings.notificationsConfig)

  const { enableInternalMetrics } = useSelector((state) => state.account.accountSettings)

  const [from, setFrom] = useState(startDate)
  const [to, setTo] = useState(endDate)
  const [compare, setCompare] = useState(compareTo)

  const cardtoggles = {
    revenue: [
      { id: NET_REVENUE, label: 'Publisher Revenue' }
    ],
    rpmCpm: [
      { id: AVG_CPM, label: 'Average CPM' }
    ],
    adRequest: [
      { id: AD_REQUEST, label: 'Ad Requests' },
      { id: TOTAL_IMPRESSIONS, label: 'Impressions' }
    ],
    bidResponse: [
      { id: BID_RATE, label: 'Bid Rate' },
      { id: WIN_RATE, label: 'Win Rate' }
    ]
  }
  const baseParams = () => {
    return {
      accountUUID,
      startDate: from,
      endDate: to,
      filters: {
        [SITE]: siteUUIDs.length ? siteUUIDs : undefined,
        [DEVICE]: devices.length ? devices : undefined,
        [GEO]: countries.length ? countries : undefined,
        [PRODUCT_TYPE]: ['PREBID_ADAPTER']
      },
      limit: 4,
      compareTo: compare
    }
  }
  const params = {
    revenue: {
      ...baseParams(),
      dimension: SITE,
      metrics: [NET_REVENUE],
      initialMetric: NET_REVENUE
    },
    rpmCpm: {
      ...baseParams(),
      dimension: SITE,
      metrics: [AVG_CPM],
      initialMetric: AVG_CPM
    },
    adRequest: {
      ...baseParams(),
      dimension: SITE,
      metrics: [AD_REQUEST, TOTAL_IMPRESSIONS],
      initialMetric: AD_REQUEST
    },
    bidResponse: {
      ...baseParams(),
      dimension: SITE,
      metrics: [BID_RATE, WIN_RATE],
      initialMetric: BID_RATE
    }
  }
  const allowedDimensions = {
    revenue: [SITE, DEVICE, GEO, AD_UNIT, REVENUE_TYPE],
    rpmCpm: [SITE, DEVICE, GEO, AD_UNIT, REVENUE_TYPE],
    adRequest: [SITE, DEVICE, GEO, AD_UNIT, REVENUE_TYPE],
    bidResponse: [SITE, DEVICE, GEO, AD_UNIT, REVENUE_TYPE]
  }

  // internal only
  if (enableInternalMetrics && mocked) {
    cardtoggles.revenue.push({ id: GROSS_REVENUE, label: 'Total Gross Revenue' })
    params.revenue.metrics.push(GROSS_REVENUE)
  }

  return (
        <Division name={'SupplySidePlatform'}>
            <Page name={'SupplySidePlatform'} klass={`${props.classes.SupplySidePlatform}`} >
                <Header>
                    <GlobalFilter onFilterApply={() => {
                      setFrom(startDate)
                      setTo(endDate)
                      setCompare(compareTo)
                    }} />
                </Header>
                <Content>
                    <PageHeader lastUpdatedTimestamp={lastUpdated}
                                title={'Monetization'}
                                subtitle={'Supply Side Platform'}
                                dataTip={true}
                                bannerText={showMessage ? message : null}
                                ignoreDataTip={showMessage}
                    />
                    <RevenueVisualization
                        params={params.revenue}
                        cardtogglesList={cardtoggles.revenue}
                        chartType={BAR_CHART}
                        allowedDimensions={allowedDimensions.revenue}
                        title={cardtoggles.revenue[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.revenue}
                    />
                    <RevenueVisualization
                        params={params.rpmCpm}
                        cardtogglesList={cardtoggles.rpmCpm}
                        chartType={BAR_CHART}
                        allowedDimensions={allowedDimensions.rpmCpm}
                        title={cardtoggles.rpmCpm[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.cpm}
                    />
                    <RevenueVisualization
                        params={params.adRequest}
                        cardtogglesList={cardtoggles.adRequest}
                        chartType={LINE_CHART}
                        allowedDimensions={allowedDimensions.adRequest}
                        title={cardtoggles.adRequest[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.adRequest}
                    />
                    <RevenueVisualization
                        params={params.bidResponse}
                        cardtogglesList={cardtoggles.bidResponse}
                        chartType={LINE_CHART}
                        allowedDimensions={allowedDimensions.bidResponse}
                        title={cardtoggles.bidResponse[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.bidResponse}
                    />
                </Content>
            </Page>
        </Division>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default SupplySidePlatformPage
